import React, { useState } from 'react';
import { BarChart2, Megaphone, Users2, FileText, MessageSquare, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MetaStatsOverviewTab from './MetaStatsOverviewTab';
import MetaStatsCampaignsTab from './MetaStatsCampaignsTab';
import MetaStatsDemographicsTab from './MetaStatsDemographicsTab';
import MetaStatsPostsTab from './MetaStatsPostsTab';
import MetaStatsCommentsTab from './MetaStatsCommentsTab';
import MetaStatsInsightsTab from './MetaStatsInsightsTab';
import MetaStatsComparisonSummary from './MetaStatsComparisonSummary';

export const META_MARKETS = ['SE', 'NO', 'FI', 'DK', 'DE'];
export const META_MARKET_FLAGS = { SE: '🇸🇪', NO: '🇳🇴', FI: '🇫🇮', DK: '🇩🇰', DE: '🇩🇪' };

const TABS = [
  { id: 'overview',      label: 'Översikt',     icon: BarChart2,     channel: 'paid' },
  { id: 'campaigns',     label: 'Kampanjer',    icon: Megaphone,     channel: 'paid' },
  { id: 'demographics',  label: 'Målgrupp',     icon: Users2,        channel: 'paid' },
  { id: 'posts',         label: 'Inlägg',       icon: FileText,      channel: 'organic' },
  { id: 'comments',      label: 'Kommentarer',  icon: MessageSquare, channel: 'organic' },
  { id: 'insights',      label: 'Insikter & rekommendationer', icon: Sparkles, channel: 'organic' },
];

export default function MetaStatsPage() {
  const { session } = useAuth();
  const accessToken = session?.access_token;
  const [tab, setTab] = useState('overview');
  const [selectedMarkets, setSelectedMarkets] = useState(META_MARKETS);

  const toggleMarket = (m) => {
    setSelectedMarkets(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const marketsParam = (selectedMarkets.length ? selectedMarkets : META_MARKETS).join(',');

  return (
    <div className="page-scroll">
      <div className="page-header">
        <div>
          <h1 className="page-title">Meta Statistik</h1>
          <p className="page-subtitle">Facebook &amp; Instagram-prestanda för Vårdväskan och Color4care</p>
        </div>
      </div>

      {tab !== 'insights' && (
        <div className="mstat-market-filter" data-testid="metastats-market-filter">
          {META_MARKETS.map(m => (
            <label
              key={m}
              className={`mstat-market-chip${selectedMarkets.includes(m) ? ' mstat-market-chip--selected' : ''}`}
              data-testid={`metastats-market-${m}`}
            >
              <input
                type="checkbox"
                className="mstat-market-chip-input"
                checked={selectedMarkets.includes(m)}
                onChange={() => toggleMarket(m)}
              />
              <span className="mstat-market-chip-flag">{META_MARKET_FLAGS[m]}</span>
              <span className="mstat-market-chip-code">{m}</span>
            </label>
          ))}
        </div>
      )}

      {accessToken && tab !== 'insights' && selectedMarkets.length > 0 && (
        <MetaStatsComparisonSummary accessToken={accessToken} marketsParam={marketsParam} />
      )}

      <div className="tab-switcher" style={{ marginBottom: 24 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`metastats-tab-${t.id}`}
            className={`tab-btn${tab === t.id ? ' tab-btn--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className={`mstat-tab-dot mstat-tab-dot--${t.channel}`} aria-hidden="true" />
            <t.icon size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'insights' ? (
        !accessToken ? <div className="admin-loading">Laddar session...</div> : <MetaStatsInsightsTab />
      ) : selectedMarkets.length === 0 ? (
        <div className="admin-empty" data-testid="metastats-no-markets">Välj minst en marknad för att visa data.</div>
      ) : !accessToken ? (
        <div className="admin-loading">Laddar session...</div>
      ) : (
        <>
          {tab === 'overview'     && <MetaStatsOverviewTab accessToken={accessToken} marketsParam={marketsParam} />}
          {tab === 'campaigns'    && <MetaStatsCampaignsTab accessToken={accessToken} marketsParam={marketsParam} />}
          {tab === 'demographics' && <MetaStatsDemographicsTab accessToken={accessToken} marketsParam={marketsParam} />}
          {tab === 'posts'        && <MetaStatsPostsTab accessToken={accessToken} marketsParam={marketsParam} />}
          {tab === 'comments'     && <MetaStatsCommentsTab accessToken={accessToken} marketsParam={marketsParam} />}
        </>
      )}
    </div>
  );
}
